/*
  Tiny sessionStorage bridge between the sign-in page (where Google
  sign-in starts) and the sign-up page (where new students finish
  registering after a 404 from `auth/social_auth`).

  Why sessionStorage and not React Router state?

    • Router state survives `<Navigate>` but is lost on a full
      window reload — and the sign-up page has a code path that
      reloads the window on success (`window.location.replace
      ('/dashboard')` after the paywall), which is fine on its own
      but would erase any state left here.
    • Putting Google email + sub on the URL would leak PII into
      browser history and any analytics / referrer logs the user's
      tab might forward.
    • sessionStorage scopes to the tab, vanishes when it closes,
      and isn't sent over the network with normal requests.

  We hold ONLY the bare minimum needed to finish registration:
  email, given/family name, and the Google `sub` (social_auth_id).
  No idToken — the BE doesn't need it for Google today (it trusts
  the social_auth_id, same as the mobile flow), and storing a JWT
  for longer than its useful lifetime is needless risk.
*/

export const GOOGLE_SIGNUP_PREFILL_KEY = "microlab_google_signup_prefill_v1";

/**
 * @typedef GoogleSignupPrefill
 * @property {string} email          — verified Google email (lowercase)
 * @property {string} firstName      — best-guess given name (or first token of full name)
 * @property {string} lastName       — best-guess family name (or remainder of full name)
 * @property {string} socialAuthId   — Google `sub` claim, used as social_auth_id
 * @property {number} createdAt      — ms epoch when stored, for staleness checks
 */

/** Persist the prefill so the sign-up screen can read it on mount. */
export function setGoogleSignupPrefill(prefill) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      GOOGLE_SIGNUP_PREFILL_KEY,
      JSON.stringify({ ...prefill, createdAt: Date.now() })
    );
  } catch {
    /* private mode / quota errors — non-fatal: the user can still type the fields manually. */
  }
}

/**
 * Read + clear the prefill in one call. Returns null when nothing is
 * cached or when the cached entry is older than 10 minutes (defensive
 * against a stale tab — we never want to autofill a Google identity
 * for a different sign-in attempt).
 */
export function consumeGoogleSignupPrefill() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(GOOGLE_SIGNUP_PREFILL_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(GOOGLE_SIGNUP_PREFILL_KEY);
    const parsed = JSON.parse(raw);
    const TEN_MINUTES = 10 * 60 * 1000;
    if (
      !parsed?.email ||
      !parsed?.socialAuthId ||
      typeof parsed.createdAt !== "number" ||
      Date.now() - parsed.createdAt > TEN_MINUTES
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}
